// WordService.js - Unified word fetching with offline fallback
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
const TIMEOUT_MS = 3000;

// Track connection status
let isOnlineMode = true;

// Create axios instance with timeout
const api = axios.create({
  baseURL: API_URL,
  timeout: TIMEOUT_MS,
});

// Check if backend is available
export const checkBackendAvailability = async () => {
  try {
    await api.get('/categories');
    isOnlineMode = true;
    return true;
  } catch (error) {
    isOnlineMode = false;
    return false;
  }
};

// Get current mode
export const isOnline = () => isOnlineMode;

// Fetch categories (only works in online mode)
export const fetchCategories = async () => {
  try {
    const response = await api.get('/categories');
    isOnlineMode = true;
    return response.data;
  } catch (error) {
    isOnlineMode = false;
    return [];
  }
};

// Fetch words by category (only works in online mode)
export const fetchWordsByCategory = async (category, limit) => {
  try {
    const response = await api.get(`/categories/${category}`, {
      params: { limit },
    });
    isOnlineMode = true;
    return response.data;
  } catch (error) {
    isOnlineMode = false;
    return [];
  }
};

// Fetch random words from any category (only works in online mode)
export const fetchWordsAnyCategory = async (limit) => {
  try {
    const response = await api.get('/words/any', {
      params: { limit },
    });
    isOnlineMode = true;
    return response.data;
  } catch (error) {
    isOnlineMode = false;
    return [];
  }
};

// Parse manual word input into the same format as API response
export const parseManualWords = (wordInput) => {
  if (!wordInput || typeof wordInput !== 'string') {
    return [];
  }

  // Split by common delimiters: newlines, commas, semicolons
  const words = wordInput
    .split(/[\n,;]+/)
    .map(word => word.trim().toUpperCase())
    .filter(word => word.length > 0 && word.length <= 15) // Filter empty and too-long words
    .filter((word, index, self) => self.indexOf(word) === index); // Remove duplicates

  // Return in the same format as the API ({ Word: string })
  return words.map(word => ({ Word: word }));
};

// Validate a single word
export const validateWord = (word) => {
  if (!word || typeof word !== 'string') {
    return { valid: false, reason: 'Word is empty' };
  }

  const trimmed = word.trim();

  if (trimmed.length === 0) {
    return { valid: false, reason: 'Word is empty' };
  }

  if (trimmed.length > 15) {
    return { valid: false, reason: 'Word is too long (max 15 characters)' };
  }

  if (!/^[a-zA-Z]+$/.test(trimmed)) {
    return { valid: false, reason: 'Word must contain only letters' };
  }

  return { valid: true, word: trimmed.toUpperCase() };
};
